import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { SEOHead } from "./components/SEOHead";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { FeedbackButton } from "./components/FeedbackButton";
import { CookieConsent } from "./components/CookieConsent";

// Eager - HomePage to entry point, ladujemy od razu zeby nie bylo dodatkowego round-tripu
import HomePage from "./pages/HomePage";

// Lazy - kazda strona jako osobny chunk. Zmniejsza bundle z 1.2 MB do ~500 KB main
// + chunk per route ladowany on-demand.
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const FriendsPage = lazy(() => import("./pages/FriendsPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const GroupsPage = lazy(() => import("./pages/GroupsPage"));
const GroupDetailPage = lazy(() => import("./pages/GroupDetailPage"));
const BoardPage = lazy(() => import("./pages/BoardPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const RankingPage = lazy(() => import("./pages/RankingPage"));
const QuizPage = lazy(() => import("./pages/QuizPage"));
const ViewersPage = lazy(() => import("./pages/ViewersPage"));
const MarriagePage = lazy(() => import("./pages/MarriagePage"));
const DedicationsPage = lazy(() => import("./pages/DedicationsPage"));
const TimeCapsulePage = lazy(() => import("./pages/TimeCapsulePage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const VotingPage = lazy(() => import("./pages/VotingPage"));
const MemoryGamePage = lazy(() => import("./pages/MemoryGamePage"));
const AchievementsPage = lazy(() => import("./pages/AchievementsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const SchoolProfilePage = lazy(() => import("./pages/SchoolProfilePage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const ZorkiPage = lazy(() => import("./pages/ZorkiPage"));
const ReferralsPage = lazy(() => import("./pages/ReferralsPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const AdvertisingPage = lazy(() => import("./pages/AdvertisingPage"));

function RouteFallback() {
    return (
        <div style={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #e2e8f0',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

function App() {
    return (
        <ErrorBoundary>
        <ThemeProvider>
            <AuthProvider>
                <SEOHead />
                <div className="App">
                    <BrowserRouter>
                    <Suspense fallback={<RouteFallback />}>
                    <Routes>
                        {/* Main pages */}
                        <Route path="/" element={<HomePage />} />
                        <Route path="/search" element={<SearchPage />} />
                        <Route path="/friends" element={<FriendsPage />} />
                        <Route path="/messages" element={<MessagesPage />} />
                        <Route path="/messages/:recipientId" element={<MessagesPage />} />
                        <Route path="/groups" element={<GroupsPage />} />
                        <Route path="/groups/:groupId" element={<GroupDetailPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/profile/:userId" element={<ProfilePage />} />

                        {/* Content pages */}
                        <Route path="/board" element={<BoardPage />} />
                        <Route path="/gallery" element={<GalleryPage />} />
                        <Route path="/blog" element={<BlogPage />} />
                        <Route path="/blog/:slug" element={<ArticlePage />} />

                        {/* Zorki */}
                        <Route path="/zorki" element={<ZorkiPage />} />
                        <Route path="/polecenia" element={<ReferralsPage />} />

                        {/* AdSense compliance: moduly w przygotowaniu (mockData) - redirect na /
                            zeby nie pokazywac uzytkownikom nieukonczonych funkcji.
                            Po dokonczeniu (Sprint G) przywrocic do Route z komponentem. */}
                        <Route path="/events" element={<Navigate to="/" replace />} />
                        <Route path="/viewers" element={<Navigate to="/" replace />} />
                        <Route path="/marriage" element={<Navigate to="/" replace />} />
                        <Route path="/dedications" element={<Navigate to="/" replace />} />
                        <Route path="/timecapsule" element={<Navigate to="/" replace />} />
                        <Route path="/voting" element={<Navigate to="/" replace />} />
                        <Route path="/ranking" element={<Navigate to="/" replace />} />
                        <Route path="/quiz" element={<Navigate to="/" replace />} />
                        <Route path="/games" element={<Navigate to="/" replace />} />
                        <Route path="/achievements" element={<Navigate to="/" replace />} />

                        {/* Polish URL aliasy (kompatybilnosc wstecz - linki zindeksowane przez Google) */}
                        <Route path="/galeria" element={<Navigate to="/gallery" replace />} />
                        <Route path="/grupy" element={<Navigate to="/groups" replace />} />
                        <Route path="/grupy/:groupId" element={<GroupDetailPage />} />
                        <Route path="/profil" element={<Navigate to="/profile" replace />} />
                        <Route path="/profil/:userId" element={<ProfilePage />} />
                        <Route path="/wiadomosci" element={<Navigate to="/messages" replace />} />
                        <Route path="/znajomi" element={<Navigate to="/friends" replace />} />
                        <Route path="/powiadomienia" element={<Navigate to="/messages" replace />} />

                        {/* Admin */}
                        <Route path="/admin" element={<AdminPage />} />

                        {/* School Profile */}
                        <Route path="/school/:schoolId" element={<SchoolProfilePage />} />
                        <Route path="/szkola/:schoolId" element={<SchoolProfilePage />} />

                        {/* Static pages */}
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/help" element={<HelpPage />} />
                        <Route path="/advertising" element={<AdvertisingPage />} />

                        {/* 404 */}
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    </Suspense>
                    </BrowserRouter>
                    <Toaster position="bottom-right" />
                    <PWAInstallPrompt />
                    <FeedbackButton />
                    <CookieConsent />
                </div>
            </AuthProvider>
        </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
